import {useEffect, useState} from "react";
import {useBlockUI} from "../../../shared/services/block-ui-service";
import instance from "../../index";
import {Driver} from "../../../models/driver";
import {MotoristaFilter} from "../../../filters/motorista-filter";
import {PagedList} from "../../../models/paged-list";
import {BaseResponse} from "../../../models/base-response";
import {ClientFilter} from "../../../filters/client-filter";
import {ShortDto} from "../../../models/short-dto";
import {CreateMotoristaRequest} from "../../../models/requests/create-motorista-request";
import {EditMotoristaRequest} from "../../../models/requests/edit-motorista-request";
import {RastreamentoLogFilter} from "../../../filters/rastreamento-log-filter";
import {TruckDriverResult} from "../../../models/truckDriverResult";
import {TruckDriverPasswordResult} from "../../../models/truck-driver-password-result";
import {ChangeMotoristaPasswordRequest} from "../../../models/requests/change-motorista-password-request";

const useDriverService = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const blockUI = useBlockUI();
    useEffect(() => {
        blockUI?.setActive(loading)
    }, [loading]);

    async function activateMotorista(id: string): Promise<Driver> {
        const response = await instance.put(`/v1/backoffice/motoristas/active/${id}`);
        return response?.data;
    }

    async function deactiveMotorista(id: string): Promise<Driver> {
        const response = await instance.put(`/v1/backoffice/motoristas/deactive/${id}`);
        return response?.data;
    }

    async function deleteMotorista(id: string) {
        const response = await instance.delete(`/v1/backoffice/motoristas/${id}`);
        return response?.data;
    }

    async function getMotoristaById(id: string): Promise<Driver> {
        const response = await instance.get<BaseResponse<Driver>>(`/v1/backoffice/motoristas/${id}`);
        return response?.data?.data;
    }

    async function getMotoristas(filter?: MotoristaFilter): Promise<PagedList<Driver>> {
        const response = await instance.get<PagedList<Driver>>('/v1/backoffice/motoristas/', {
            params: filter
        });
        return response?.data;
    }
    async function getAllDriversShortDto(filter?: MotoristaFilter): Promise<ShortDto[]> {
        const response = await instance.get<BaseResponse<ShortDto[]>>('/v1/backoffice/motoristas/all', {
            params: filter
        });
        return response?.data?.data;
    }

    async function getAllFilteredDriversShortDto(filter?: MotoristaFilter): Promise<ShortDto[]> {
        const response = await instance.get<BaseResponse<ShortDto[]>>('/v1/backoffice/motoristas/all/select', {
            params: filter
        });
        return response?.data?.data;
    }

    async function createMotorista(request: Partial<CreateMotoristaRequest>): Promise<BaseResponse<TruckDriverResult[]>> {
        const response = await instance.post<BaseResponse<TruckDriverResult[]>>(`/v1/backoffice/motoristas/`, request);
        return response?.data;
    }

    async function editMotorista(request: Partial<EditMotoristaRequest>, id: string | string[] | undefined): Promise<Driver> {
        const response = await instance.put<Driver>(`/v1/backoffice/motoristas/${id}`, request);
        return response?.data;
    }

    async function getAllFilteredRastreadorLogsDriversName(filter?: RastreamentoLogFilter): Promise<ShortDto[]> {
        const response = await instance.get<BaseResponse<ShortDto[]>>('/v1/backoffice/logs/all', {
            params: filter
        });
        return response?.data?.data;
    }

    async function resetMotoristaPassword(id: string): Promise<TruckDriverPasswordResult> {
        setLoading(true);
        const response = await instance
            .put<BaseResponse<TruckDriverPasswordResult>>(`/v1/backoffice/motoristas/${id}/reset-password`)
            .finally(() => setLoading(false));
        return response.data.data;
    }

    async function changeMotoristaPassword(
        id: string,
        request: ChangeMotoristaPasswordRequest
    ): Promise<TruckDriverPasswordResult> {
        setLoading(true);
        const response = await instance
            .put<BaseResponse<TruckDriverPasswordResult>>(`/v1/backoffice/motoristas/${id}/password`, request)
            .finally(() => setLoading(false));
        return response.data.data;
    }

    return {
        activateMotorista,
        deactiveMotorista,
        deleteMotorista,
        getMotoristaById,
        getMotoristas,
        getAllDriversShortDto,
        createMotorista,
        editMotorista,
        getAllFilteredDriversShortDto,
        getAllFilteredRastreadorLogsDriversName,
        resetMotoristaPassword,
        changeMotoristaPassword
    };
};
export default useDriverService;